/**
 * useClickOutside Hook
 *
 * A reusable React hook that detects clicks outside a specified element and triggers a callback.
 * Commonly used for closing dropdowns, modals, or menus when clicking outside.
 *
 * @param ref - React ref object pointing to the element to monitor
 * @param isActive - Boolean flag to enable/disable the click outside detection
 * @param onClickOutside - Callback function to execute when clicking outside
 *
 * @example
 * ```tsx
 * import { useClickOutside } from './hooks/useClickOutside';
 *
 * const MyDropdown = () => {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useRef<HTMLDivElement>(null);
 *
 *   useClickOutside(dropdownRef, isOpen, () => setIsOpen(false));
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       <button onClick={() => setIsOpen(!isOpen)}>Toggle</button>
 *       {isOpen && <div>Dropdown content</div>}
 *     </div>
 *   );
 * };
 * ```
 *
 * @features
 * - Automatically adds/removes event listeners based on isActive state
 * - Handles both mouse and touch events for mobile compatibility
 * - Prevents memory leaks by cleaning up listeners on unmount
 * - Ignores clicks on the referenced element itself
 */
import { useEffect, RefObject } from 'react';

export const useClickOutside = <T extends HTMLElement>(
  ref: RefObject<T>,
  isOpen: boolean,
  onClose: () => void,
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [ref, isOpen, onClose]);
};
